import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, CheckCircle2Icon, CreditCardIcon, CalendarIcon, TagIcon, TicketIcon, LayersIcon, CalculatorIcon, ReceiptIcon, UsersIcon, CodeIcon, ShieldCheckIcon } from 'lucide-react';
import type { WizardState } from '../../lib/wizardUtils';
interface CopilotSummaryProps {
  data: {
    buildingType: string;
    chargeModel: string;
    pricingTiers: string;
    products: string;
    billingFrequency: string;
    freeTrial: string;
    coupons: string;
    tax: string;
    invoices: string;
    billingManager: string;
    techStack: string;
    stripeAccount: string;
  };
}
function parseProducts(input: string) {
  const products = [];
  const currency = input.includes('£') ? 'GBP' : input.includes('€') ? 'EUR' : 'USD';
  const parts = input.split(/,|\+|and/i);
  parts.forEach(part => {
    const text = part.trim();
    if (!text) return;
    const priceMatch = text.match(/[\$£€]?\s*(\d+)/);
    const price = priceMatch ? parseInt(priceMatch[1]) : 0;
    let billingType: 'recurring_monthly' | 'recurring_annual' | 'recurring_both' | 'one_time' = 'one_time';
    if (text.toLowerCase().includes('mo') || text.toLowerCase().includes('month')) {
      billingType = 'recurring_monthly';
      if (text.toLowerCase().includes('year') || text.toLowerCase().includes('annual')) {
        billingType = 'recurring_both';
      }
    } else if (text.toLowerCase().includes('year') || text.toLowerCase().includes('annual')) {
      billingType = 'recurring_annual';
    } else if (text.toLowerCase().includes('recurring')) {
      billingType = 'recurring_monthly';
    }
    let name = 'Product';
    if (priceMatch && priceMatch.index && priceMatch.index > 0) {
      name = text.substring(0, priceMatch.index).trim();
      name = name.replace(/[\$£€]$/, '').replace(/for$/, '').trim();
    }
    if (!name) name = 'Product';
    if (price > 0) {
      products.push({
        name,
        price,
        currency,
        billingType
      });
    }
  });
  if (products.length === 0) {
    products.push({
      name: 'Custom Plan',
      price: 99,
      currency: 'USD',
      billingType: 'recurring_monthly'
    });
  }
  return products;
}
export function CopilotSummary({
  data
}: CopilotSummaryProps) {
  const navigate = useNavigate();
  const handleViewRecommendation = () => {
    const hasTrials = data.freeTrial.toLowerCase().includes('yes');
    const hasCoupons = data.coupons.toLowerCase().includes('yes');
    const hasTax = data.tax.toLowerCase().includes('yes');
    const hasInvoices = data.invoices.toLowerCase().includes('yes');
    let billingManager = 'Customers self-serve';
    if (data.billingManager.toLowerCase().includes('admin')) billingManager = 'Admin manages';
    if (data.billingManager.toLowerCase().includes('both')) billingManager = 'Both';
    let frontend = 'Next.js';
    let backend = 'Node.js';
    let database = 'PostgreSQL';
    if (data.techStack && data.techStack.includes('+')) {
      const parts = data.techStack.split('+').map(s => s.trim());
      if (parts.length >= 1) frontend = parts[0];
      if (parts.length >= 2) backend = parts[1];
      if (parts.length >= 3) database = parts[2];
    } else if (data.techStack && !data.techStack.includes('Let me type')) {
      frontend = data.techStack;
    }
    let stripeAccount = 'Not sure';
    if (data.stripeAccount.toLowerCase().includes('yes')) stripeAccount = 'Yes';
    if (data.stripeAccount.toLowerCase().includes('no')) stripeAccount = 'No';
    const parsedProducts = parseProducts(data.products || '');
    const wizardState: WizardState = {
      buildingType: data.buildingType,
      chargeModel: data.chargeModel,
      features: {
        trials: hasTrials,
        coupons: hasCoupons,
        tax: hasTax,
        invoices: hasInvoices
      },
      billingManager,
      stack: {
        frontend,
        backend,
        database
      },
      stripeAccount,
      stripeAccountId: '',
      products: parsedProducts
    };
    localStorage.setItem('paykit_wizard_state', JSON.stringify(wizardState));
    navigate('/setup/recommendation');
  };
  return <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm animate-slide-in" data-id="element-58">
      <div className="p-5 space-y-3" data-id="element-59">
        <div className="flex items-center gap-3 p-3 bg-pk-surface rounded-lg" data-id="element-60">
          <LayersIcon className="w-4 h-4 text-primary shrink-0" data-id="element-61" />
          <div className="flex-1 min-w-0" data-id="element-62">
            <div className="text-xs text-muted-foreground" data-id="element-63">Product type</div>
            <div className="text-sm font-medium truncate" data-id="element-64">
              {data.buildingType}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-pk-surface rounded-lg" data-id="element-65">
          <TagIcon className="w-4 h-4 text-primary shrink-0" data-id="element-66" />
          <div className="flex-1 min-w-0" data-id="element-67">
            <div className="text-xs text-muted-foreground" data-id="element-68">Pricing tiers</div>
            <div className="text-sm font-medium truncate" data-id="element-69">
              {data.pricingTiers}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-pk-surface rounded-lg" data-id="element-70">
          <CreditCardIcon className="w-4 h-4 text-primary shrink-0" data-id="element-71" />
          <div className="flex-1 min-w-0" data-id="element-72">
            <div className="text-xs text-muted-foreground" data-id="element-73">
              Products & Prices
            </div>
            <div className="text-sm font-medium truncate" data-id="element-74">
              {data.products || 'Not specified'}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-pk-surface rounded-lg" data-id="element-75">
          <CalendarIcon className="w-4 h-4 text-primary shrink-0" data-id="element-76" />
          <div className="flex-1 min-w-0" data-id="element-77">
            <div className="text-xs text-muted-foreground" data-id="element-78">
              Billing frequency
            </div>
            <div className="text-sm font-medium truncate" data-id="element-79">
              {data.billingFrequency}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-pk-surface rounded-lg" data-id="element-80">
          <UsersIcon className="w-4 h-4 text-primary shrink-0" data-id="element-81" />
          <div className="flex-1 min-w-0" data-id="element-82">
            <div className="text-xs text-muted-foreground" data-id="element-83">Billing manager</div>
            <div className="text-sm font-medium truncate" data-id="element-84">
              {data.billingManager}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-pk-surface rounded-lg" data-id="element-85">
          <CodeIcon className="w-4 h-4 text-primary shrink-0" data-id="element-86" />
          <div className="flex-1 min-w-0" data-id="element-87">
            <div className="text-xs text-muted-foreground" data-id="element-88">Tech stack</div>
            <div className="text-sm font-medium truncate" data-id="element-89">{data.techStack}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-pk-surface rounded-lg" data-id="element-90">
          <ShieldCheckIcon className="w-4 h-4 text-primary shrink-0" data-id="element-91" />
          <div className="flex-1 min-w-0" data-id="element-92">
            <div className="text-xs text-muted-foreground" data-id="element-93">Stripe account</div>
            <div className="text-sm font-medium truncate" data-id="element-94">
              {data.stripeAccount}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3" data-id="element-95">
          <div className="flex items-center gap-2 p-3 bg-pk-surface rounded-lg" data-id="element-96">
            <CheckCircle2Icon className={`w-4 h-4 shrink-0 ${data.freeTrial.toLowerCase().includes('yes') ? 'text-pk-success' : 'text-muted-foreground'}`} data-id="element-97" />
            <div data-id="element-98">
              <div className="text-xs text-muted-foreground" data-id="element-99">Free trial</div>
              <div className="text-sm font-medium" data-id="element-100">
                {data.freeTrial.toLowerCase().includes('yes') ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-pk-surface rounded-lg" data-id="element-101">
            <TicketIcon className={`w-4 h-4 shrink-0 ${data.coupons.toLowerCase().includes('yes') ? 'text-pk-success' : 'text-muted-foreground'}`} data-id="element-102" />
            <div data-id="element-103">
              <div className="text-xs text-muted-foreground" data-id="element-104">Coupons</div>
              <div className="text-sm font-medium" data-id="element-105">
                {data.coupons.toLowerCase().includes('yes') ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-pk-surface rounded-lg" data-id="element-106">
            <CalculatorIcon className={`w-4 h-4 shrink-0 ${data.tax.toLowerCase().includes('yes') ? 'text-pk-success' : 'text-muted-foreground'}`} data-id="element-107" />
            <div data-id="element-108">
              <div className="text-xs text-muted-foreground" data-id="element-109">Tax</div>
              <div className="text-sm font-medium" data-id="element-110">
                {data.tax.toLowerCase().includes('yes') ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-pk-surface rounded-lg" data-id="element-111">
            <ReceiptIcon className={`w-4 h-4 shrink-0 ${data.invoices.toLowerCase().includes('yes') ? 'text-pk-success' : 'text-muted-foreground'}`} data-id="element-112" />
            <div data-id="element-113">
              <div className="text-xs text-muted-foreground" data-id="element-114">Invoices</div>
              <div className="text-sm font-medium" data-id="element-115">
                {data.invoices.toLowerCase().includes('yes') ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border p-4" data-id="element-116">
        <button onClick={handleViewRecommendation} className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:opacity-90 px-6 py-3 rounded-lg font-medium transition-all shadow-lg shadow-primary/20" data-id="element-117">
          View My Recommendation
          <ArrowRightIcon className="w-4 h-4" data-id="element-118" />
        </button>
      </div>
    </div>;
}